// fraction.js
//
// Fractions in a number a person types, and in a number keepr shows: the
// reader behind "1 1/2 cups" and "3 1/4 in", and the writer behind an element
// whose "Show as a fraction" is on (`options.fractions`, KPR-1025). A STORED
// VALUE IS ALWAYS A NUMBER: a fraction is a way of spelling one, read on the
// way in and written on the way out, never kept.
//
// ONE FILE, FOUR COPIES. This file is vendored byte-identical, and lives in
// two folders of each repo because the two spellers that need it cannot reach
// each other: the units folder (keepr-api `utils/units/`, keepr-web
// `src/utils/units/vendored/`) and elementFormat (`utils/elementFormat/` in
// both) sit at different depths in the two repos, so a require() between them
// would resolve in one repo and not the other. Each repo's suite pins its two
// copies equal (keepr-api test/fractionCopies.test.js, keepr-web
// src/utils/elementFormat/__tests__/fraction.test.js), and the parity suites
// pin both pairs across the repos. Edit one, copy it to the other three.
//
// Pure and dependency-free, like everything it sits beside: nothing here may
// require() anything, or know about Mongo, Boom or the request.
//
// READING (readFraction). Text with no fraction mark -- no slash and no
// fraction character -- is not ours: null, and the caller reads it as it
// always has ("0.5", "1,234", "12.5%"). Nor is a slash with no digit near it
// ("n/a", "and/or"): a word, refused by the caller as the word it is. Text WITH one is either a fraction or
// a refusal, never a guess:
//
//   "3/4"  "-3/4"  "+3/4"  "3/2"      a plain fraction (top over bottom)
//   "1 1/2"  "1-1/2"  "-1 1/2"        a mixed number: whole, then the
//                                     fraction, apart by spaces or one hyphen
//   "3 / 4"  "1 1 / 2"                spaces around the slash
//   1 U+2044 2, 1 U+2215 2            the fraction slash and the division
//                                     slash, read as '/'
//   U+00BD, 1 U+00BD, 1-U+00BD,       a fraction character (one half, three
//   -U+00BE                           quarters: VULGAR below), alone or after
//                                     a whole number, apart by nothing,
//                                     spaces or one hyphen
//
// A sign goes only at the very front, and belongs to the whole value:
// "-1 1/2" is -1.5. The top and bottom are whole numbers. Every refusal is a
// short reason the caller puts after its own words ('expected a number, got
// "1/0": ' + reason), because the person reading it is fixing what they
// typed:
//
//   "1/0"  "0/0"         a fraction cannot have 0 under the line
//   "1//2"  "1/2/3"      a fraction has one slash
//   "/2"  "1/"  "1 /"    a number goes above and below the line
//   "1.5/2"  "1,5/2"     above and below the line are whole numbers
//   "1 -1/2"  "3/-4"     a sign goes only at the front
//   "1 3/2", 1 U+00BD U+00BD   after a whole number the fraction is less
//                              than one
//
// Text longer than MAX_FRACTION_TEXT is refused before any pattern runs, and
// every pattern is anchored with no two parts able to match the same
// characters, so a stranger's padded input costs linear time on the anonymous
// public form as everywhere else (the lesson of typedElements NUMERIC_STRING).
//
// WRITING (nearestFraction / formatFraction). Halves, thirds, quarters and
// eighths -- the fractions a measuring cup, a tape measure and a recipe use --
// in lowest terms: 0.5 -> "1/2", 1.5 -> "1 1/2", 0.75 -> "3/4", 2.375 ->
// "2 3/8", -0.25 -> "-1/4". A value within DISPLAY_TOLERANCE of one reads as
// it; anything else is not a fraction and the caller spells the decimal it
// always did (formatFraction answers null).
//
// THE TOLERANCE IS 0.005, half a hundredth (plus FLOAT_SLACK for binary
// dust). It is that wide so a value STORED at two decimals still reads as
// its fraction: 1/3 kept at two decimals is 0.33 (0.0033 away), 1/8 is 0.13
// (0.005 away). It can be no wider without ambiguity mattering: the two
// closest candidates, 1/3 and 3/8, are 0.0417 apart, so no value is ever
// within the tolerance of two different fractions. A value that is nearest a
// fraction by a hair more (0.0051 away) is not one, and keeps its decimal.
// One exception: a value that is NOT zero never reads as "0" (0.004 is not
// nothing); it keeps its decimal.

'use strict';

// The longest text read as a fraction. "-123,456,789 12345/67890" is 24; this
// leaves room for spaces and stays far below anything slow.
const MAX_FRACTION_TEXT = 64;

// What a fraction is shown in, smallest first: on a tie the smaller wins
// (lowest terms are taken after, so 4/8 is never chosen over 1/2 anyway).
const DISPLAY_DENOMINATORS = Object.freeze([2, 3, 4, 8]);
const DISPLAY_TOLERANCE = 0.005;
const FLOAT_SLACK = 1e-9;

// The fraction characters Unicode spells as one character, by their top and
// bottom, written as escapes so the code is ASCII whatever the editor.
const VULGAR = Object.freeze({
	'\u00bc': [1, 4], '\u00bd': [1, 2], '\u00be': [3, 4],
	'\u2150': [1, 7], '\u2151': [1, 9], '\u2152': [1, 10],
	'\u2153': [1, 3], '\u2154': [2, 3],
	'\u2155': [1, 5], '\u2156': [2, 5], '\u2157': [3, 5], '\u2158': [4, 5],
	'\u2159': [1, 6], '\u215a': [5, 6],
	'\u215b': [1, 8], '\u215c': [3, 8], '\u215d': [5, 8], '\u215e': [7, 8],
	'\u2189': [0, 3],
});
const VULGAR_CLASS = '[' + Object.keys(VULGAR).join('') + ']';

// The two slashes that are not '/' but mean it: U+2044 FRACTION SLASH (what
// a word processor puts between the numbers when it makes 1/2 pretty) and
// U+2215 DIVISION SLASH.
const OTHER_SLASHES = /[\u2044\u2215]/g;

// A mixed number's whole part: digits, or en-US grouping ("1,234") -- the
// spelling number.js writes for a grouped element shown as a fraction
// ("1,234 1/2"), which must read back as what it shows. The grouped form
// needs a comma after one to three digits, so the two never match the same
// text the same way.
const WHOLE = '(\\d{1,3}(?:,\\d{3})+|\\d+)';
// A plain or mixed fraction: [sign] [whole (spaces | -)] top [ ]/[ ] bottom.
// Each part starts where the one before had to end (a run of digits, then a
// space or a hyphen; digits, then the slash), so the match is linear.
const SLASH_FRACTION = new RegExp('^([+-]?)(?:' + WHOLE + '(?:\\s+|-))?(\\d+)\\s*\\/\\s*(\\d+)$');
// A fraction character, alone or after a whole number.
const VULGAR_FRACTION = new RegExp('^([+-]?)(?:' + WHOLE + '(?:\\s+|-)?)?(' + VULGAR_CLASS + ')$');
// Does the text carry a fraction mark at all?
const FRACTION_MARK = new RegExp('[/\\u2044\\u2215]|' + VULGAR_CLASS);

// The same two shapes at the START of a longer text, for a reader that has
// more to read after the number (a measurement's unit: "1 1/2 cups"). Only
// the shapes; the reading is readFraction's. The negative look-ahead keeps a
// fraction from stopping in the middle of a run of digits ("1/23" is never
// "1/2" and "3").
const SLASH_FRACTION_HEAD = /^[+-]?(?:\d+(?:\s+|-))?\d+\s*\/\s*\d+(?!\d)/;
const VULGAR_FRACTION_HEAD = new RegExp('^[+-]?(?:\\d+(?:\\s+|-)?)?' + VULGAR_CLASS);

function normalize(text) {
	return text.replace(OTHER_SLASHES, '/').trim();
}

// A digit or a fraction character: text with neither ("n/a", "and/or") is a
// word with a slash in it, not a fraction anybody typed, and is left to the
// caller's own refusal.
const FRACTION_CONTENT = new RegExp('\\d|' + VULGAR_CLASS);

/**
 * Does `text` look like a fraction attempt: a fraction mark (a slash or a
 * fraction character) and at least one digit or fraction character?
 */
function hasFractionMark(text) {
	return typeof text === 'string' && FRACTION_MARK.test(text) && FRACTION_CONTENT.test(text);
}

// Why text with a fraction mark is not a fraction, as a reason the caller
// finishes its own sentence with. The checks run from the most specific
// mistake to the least, on text already known not to be a fraction.
function problemOf(text) {
	const slashes = (text.match(/\//g) || []).length;
	if (slashes > 1) return 'a fraction has one slash, like 3/4 or 1 1/2';
	if (/[.,]/.test(text)) return 'the numbers above and below the line are whole numbers, like 3/4 or 1 1/2';
	// Past the one sign at the front and the one hyphen of "1-1/2", a sign is
	// in the wrong place: "1 -1/2", "3/-4", "1--1/2".
	const unsigned = text.replace(/^[+-]/, '').replace(/^(\d+)-(?=\d)/, '$1 ');
	if (/[+-]/.test(unsigned)) return 'a sign goes only at the very front, like -1 1/2';
	if (/^\s*\//.test(unsigned) || /\/\s*$/.test(unsigned)) {
		return 'a fraction needs a number above and below the line, like 3/4';
	}
	return 'that is not a fraction keepr reads: write it like 3/4, 1 1/2 or 1-1/2';
}

function withSign(sign, value) {
	// -0/4 is 0, not -0: no sign survives a zero.
	if (value === 0) return 0;
	return sign === '-' ? -value : value;
}

/**
 * readFraction(text) -> null | { value } | { error }
 *
 * null when `text` is not a string or carries no fraction mark: the caller
 * reads it as it always has. `{ value }`, a finite number, for a fraction
 * keepr reads. `{ error }`, a reason in lower case with no full stop, for
 * text that has a fraction mark and is not one.
 */
function readFraction(raw) {
	if (typeof raw !== 'string' || !hasFractionMark(raw)) return null;
	if (raw.length > MAX_FRACTION_TEXT) return { error: 'that is too long to be a fraction' };
	const text = normalize(raw);

	const slash = text.match(SLASH_FRACTION);
	if (slash) {
		const whole = slash[2] === undefined ? null : Number(slash[2].replace(/,/g, ''));
		const top = Number(slash[3]);
		const bottom = Number(slash[4]);
		if (bottom === 0) return { error: 'a fraction cannot have 0 under the line' };
		if (whole !== null && top >= bottom) {
			return { error: 'after a whole number the fraction is less than one, like 1 1/2' };
		}
		const value = (whole === null ? 0 : whole) + top / bottom;
		if (!Number.isFinite(value)) return { error: 'that is too large to be a number' };
		return { value: withSign(slash[1], value) };
	}

	const vulgar = text.match(VULGAR_FRACTION);
	if (vulgar) {
		const pair = VULGAR[vulgar[3]];
		const whole = vulgar[2] === undefined ? 0 : Number(vulgar[2].replace(/,/g, ''));
		const value = whole + pair[0] / pair[1];
		if (!Number.isFinite(value)) return { error: 'that is too large to be a number' };
		return { value: withSign(vulgar[1], value) };
	}

	// Two fraction characters, or one beside a slash.
	const characters = text.match(new RegExp(VULGAR_CLASS, 'g')) || [];
	if (characters.length > 1 || (characters.length === 1 && text.indexOf('/') !== -1)) {
		return { error: 'after a whole number the fraction is less than one, like 1 1/2' };
	}
	return { error: problemOf(text) };
}

/**
 * fractionHead(text) -> the leading part of `text` that has a fraction's
 * shape, or null. For a reader with more to read after the number: "1 1/2
 * cups" -> "1 1/2", U+00BD + " tsp" -> U+00BD. The part still goes through
 * readFraction (a "1/0" head has the shape and is refused there).
 */
function fractionHead(raw) {
	if (typeof raw !== 'string' || raw.length > MAX_FRACTION_TEXT || !hasFractionMark(raw)) return null;
	const text = raw.replace(OTHER_SLASHES, '/');
	const match = text.match(SLASH_FRACTION_HEAD) || text.match(VULGAR_FRACTION_HEAD);
	return match ? match[0] : null;
}

function greatestDivisor(a, b) {
	let x = a;
	let y = b;
	while (y) {
		const t = y;
		y = x % y;
		x = t;
	}
	return x;
}

/**
 * nearestFraction(num) -> { negative, whole, numerator, denominator } | null
 *
 * The halves-thirds-quarters-eighths reading of `num` within
 * DISPLAY_TOLERANCE, in lowest terms (numerator 0 means a whole number), or
 * null when it has none -- or when `num` is not finite or is past
 * Number.MAX_SAFE_INTEGER, where the digits after the point are no longer
 * held at all. A fraction that rounds up to a whole carries: 1.999 -> 2.
 */
function nearestFraction(num) {
	if (typeof num !== 'number' || !Number.isFinite(num)) return null;
	const magnitude = Math.abs(num);
	if (magnitude > Number.MAX_SAFE_INTEGER) return null;
	let whole = Math.floor(magnitude);
	const rest = magnitude - whole;
	let best = null;
	for (const denominator of DISPLAY_DENOMINATORS) {
		const numerator = Math.round(rest * denominator);
		const distance = Math.abs(rest - numerator / denominator);
		if (best === null || distance < best.distance) best = { numerator, denominator, distance };
	}
	if (best.distance > DISPLAY_TOLERANCE + FLOAT_SLACK) return null;
	let numerator = best.numerator;
	let denominator = best.denominator;
	if (numerator === denominator) {
		whole += 1;
		numerator = 0;
	}
	if (numerator === 0) {
		denominator = 1;
	} else {
		const divisor = greatestDivisor(numerator, denominator);
		numerator /= divisor;
		denominator /= divisor;
	}
	return { negative: num < 0 && (whole > 0 || numerator > 0), whole, numerator, denominator };
}

function groupDigits(digits) {
	return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * spellFraction(parts, { group }) -> "1 1/2" / "3/4" / "2" / "-1/4"
 *
 * A nearestFraction answer as text: the whole number (grouped in threes with
 * `group`), a space, the fraction. ASCII digits and slash, so it reads the
 * same in an export, a title and a screen reader.
 */
function spellFraction(parts, options) {
	const group = !!(options && options.group);
	const sign = parts.negative ? '-' : '';
	const wholeText = group ? groupDigits(String(parts.whole)) : String(parts.whole);
	if (parts.numerator === 0) return sign + wholeText;
	const fractionText = parts.numerator + '/' + parts.denominator;
	return sign + (parts.whole === 0 ? fractionText : wholeText + ' ' + fractionText);
}

/**
 * formatFraction(num, { group }) -> "1 1/2" | null
 *
 * `num` spelled as a fraction, or null when it is not near one (the caller
 * spells its decimal) -- and null for a value that is not zero but would
 * read as "0".
 */
function formatFraction(num, options) {
	const parts = nearestFraction(num);
	if (!parts) return null;
	if (parts.whole === 0 && parts.numerator === 0 && num !== 0) return null;
	return spellFraction(parts, options);
}

module.exports = {
	MAX_FRACTION_TEXT,
	DISPLAY_DENOMINATORS,
	DISPLAY_TOLERANCE,
	VULGAR,
	hasFractionMark,
	readFraction,
	fractionHead,
	nearestFraction,
	spellFraction,
	formatFraction,
};
